import React, { useContext, useState } from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';
import { Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import store, { useAppDispatch } from 'redux/store';
import { ensureTranslationPreference } from 'redux/actions/users';

import { AngularContext, AngularServicesContext } from 'react-app';
import useAngularExpression from 'shared/hooks/use-angular-expression';
import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';

import { gray1, warning } from 'styles/global_defaults/colors';
import { standardSpacing } from 'styles/global_defaults/scaffolding';

// components
import NvIcon from 'shared/components/nv-icon';
import { LecturePageMode } from 'lecture_pages/components';
import { config } from '@config/config.json';

type EditModeCenterButtonProps = {
  exitToPreviousMode: (saveReorder?: boolean) => void;
};

const EditModeCenterButton = ({ exitToPreviousMode }: EditModeCenterButtonProps) => {
  // Necessary to reload translations
  const catalogId = useSelector((state) => state.app.currentCatalogId);
  const { injectServices } = useContext(AngularContext);
  const [CurrentUserManager] = injectServices(['CurrentUserManager']);
  const dispatch = useAppDispatch();

  const styles = css`
    .circle {
      width: 16px;
      height: 16px;
      border-radius: 50%;
      background-color: ${warning};

    }
    .edit-center-button {
      border-radius: ${standardSpacing}px;
      background-color: ${gray1};
      border: none;
      box-shadow: none;
    }

    .bs4-btn-primary {
      &:hover {
        background-color: ${gray1} !important;
      }

      &:not(.disabled):focus {
        box-shadow: none !important;
      }
    }
  `;

  const clickHandler = () => {
    if (catalogId) {
      dispatch(ensureTranslationPreference({ catalogId })).then(() => {
        const currentUser = store.getState().models.users[CurrentUserManager.user.id];
        if (CurrentUserManager.user.translationPreferenceLanguage !== currentUser.translationPreferenceLanguage) {
          CurrentUserManager.user.updateFromReact({
            translationPreferenceLanguage: currentUser.translationPreferenceLanguage,
            lastTranslationPreferenceLanguage: currentUser.lastTranslationPreferenceLanguage,
          });
        }
      });
    }
    exitToPreviousMode();
  };

  const params = useLecturePageParams();
  const [isHover, setIsHover] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const { $state } = useContext(AngularServicesContext);
  const currentStateName = useAngularExpression(() => $state.current.name);
  const isCourseHome = currentStateName === 'course-home'
    || currentStateName === 'course-home-edit'
    || currentStateName === 'program-home'
    || currentStateName === 'program-timelines-edit';

  let buttonText = t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.EDIT_MODE_ON();
  let dataQa = config.pendo.lectureEdit.editMenuTopPanel.editModeCenterButton;

  if (params.mode === LecturePageMode.RESTRICTED_EDIT) {
    buttonText = t.LECTURE_PAGES.ADMIN.EDIT_MENU_TOP_HEADER.ADMIN_MODE_ON();
    dataQa = config.pendo.lectureEdit.editMenuTopPanel.adminModeCenterButton;
  }

  if (isCourseHome) {
    dataQa = config.pendo.course.exitEditModeCenterButton;
  }

  return (
    <div css={styles}>
      <Button
        className='edit-center-button d-flex p-2 align-items-center'
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onClick={() => clickHandler()}
        aria-label={buttonText}
        data-qa={dataQa}
      >
        {(isHover || isFocused)
          ? (<NvIcon icon='close' size='smallest' className='text-gray-5 mr-1' />)
          : (<div className='circle mr-1' />)}
        <span className='label text-gray-5'>{buttonText}</span>
      </Button>
    </div>
  );
};

export default EditModeCenterButton;
